import {
  Children,
  cloneElement,
  isValidElement,
  useCallback,
  useState,
} from "react";
import posthog from "posthog-js";
import { getNewFeatureById } from "../catalog";
import { isFeatureSeen } from "../seenRepository";
import { useNewFeaturesContext } from "../newFeaturesContext";
import { useVisibleAnchor } from "../hooks/useVisibleAnchor";
import Button from "../../../components/Buttons/Button";
import NewFeatureCallout from "./NewFeatureCallout";

/**
 * @param {...import("react").Ref<HTMLElement>} refs
 * @returns {(node: HTMLElement | null) => void}
 */
const mergeRefs = (...refs) => {
  return (node) => {
    refs.forEach((ref) => {
      if (typeof ref === "function") {
        ref(node);
        return;
      }

      if (ref) {
        ref.current = node;
      }
    });
  };
};

/**
 * @param {{
 *   featureId: string,
 *   placement?: "right" | "left" | "top" | "bottom",
 *   children: import("react").ReactElement,
 * }} props
 */
const BoundNewFeatureTooltip = ({ featureId, placement, children }) => {
  const context = useNewFeaturesContext();
  const [anchorEl, setAnchorEl] = useState(null);
  const feature = getNewFeatureById(featureId);
  const isVisible = useVisibleAnchor(anchorEl);

  const setAnchorRef = useCallback((node) => {
    setAnchorEl(node);
  }, []);

  const { seenById, isSeenReady, markSeen, uid } = context ?? {};

  const onDismiss = useCallback(
    (event) => {
      event?.stopPropagation?.();
      event?.preventDefault?.();
      markSeen?.(featureId);
    },
    [featureId, markSeen]
  );

  const onDiscover = useCallback(
    (event) => {
      event?.stopPropagation?.();
      event?.preventDefault?.();

      posthog.capture("new_feature_callout_discover", {
        feature_id: featureId,
      });

      markSeen?.(featureId);
      // Navigate / open the feature after marking seen; click is intentional.
      anchorEl?.click();
    },
    [anchorEl, featureId, markSeen]
  );

  const child = Children.only(children);

  if (!isValidElement(child)) {
    return children;
  }
  const mergedRef = mergeRefs(setAnchorRef, child.ref);
  const anchorRefProps =
    child.type === Button ? { refButton: mergedRef } : { ref: mergedRef };

  if (!feature?.enabled) {
    return child;
  }

  const shouldShow =
    Boolean(uid) &&
    Boolean(isSeenReady) &&
    !isFeatureSeen(featureId, seenById) &&
    isVisible;

  const resolvedPlacement = placement ?? feature.placement ?? "right";

  return (
    <>
      {cloneElement(child, {
        ...anchorRefProps,
        onClick: (event) => {
          markSeen?.(featureId);
          child.props.onClick?.(event);
        },
      })}
      <NewFeatureCallout
        feature={feature}
        anchorEl={anchorEl}
        open={shouldShow}
        placement={resolvedPlacement}
        onDismiss={onDismiss}
        onDiscover={onDiscover}
      />
    </>
  );
};

/**
 * @param {{
 *   featureId?: string,
 *   placement?: "right" | "left" | "top" | "bottom",
 *   children: import("react").ReactNode,
 * }} props
 */
const NewFeatureTooltip = ({ featureId, placement, children }) => {
  if (!featureId) {
    return children;
  }

  return (
    <BoundNewFeatureTooltip featureId={featureId} placement={placement}>
      {children}
    </BoundNewFeatureTooltip>
  );
};

export default NewFeatureTooltip;
